import { bmiCalculatorKeys as k } from "@/i18n/bmi-calculator-keys"

/**
 * BMI classification for adults, per the World Health Organization cut-offs.
 * Pure data + arithmetic, kept out of the widget so both the interactive
 * result card and the static reference table read the same source, and so the
 * maths is unit-testable without rendering React.
 *
 * The ranges are the standard adult ones and are NOT valid for children,
 * pregnancy or competitive athletes — the page says so in `bmi_limit4Body`.
 */

export type BmiCategory = BmiCategoryDef["id"]

/** Lower bound of the healthy adult range, and the upper bound used for it. */
export const HEALTHY_BMI_MIN = 18.5
export const HEALTHY_BMI_MAX = 24.9

/**
 * The six adult bands, ordered low to high — `bmiCategoryFor` walks this in
 * order.
 *
 * `as const` is load-bearing. Annotating the `*Key` fields as
 * `MessageKeys<"bmiCalculator">` widens them to the namespace's whole key
 * union, which includes `bmi_calcHealthyRangeValue` — a message with ICU
 * placeholders. next-intl's typed `t()` then demands a `values` argument at
 * every `t(category.labelKey)` call site (TS2554), even though none of the
 * keys actually stored here take one. Keeping the literals narrows the union
 * to six placeholder-free keys.
 */
const CATEGORY_DEFS = [
  {
    id: "underweight",
    max: 18.5,
    labelKey: k.catUnderweight,
    rangeKey: k.rangeUnderweight,
    meaningKey: k.meaningUnderweight,
  },
  {
    id: "healthy",
    max: 25,
    labelKey: k.catHealthy,
    rangeKey: k.rangeHealthy,
    meaningKey: k.meaningHealthy,
  },
  {
    id: "overweight",
    max: 30,
    labelKey: k.catOverweight,
    rangeKey: k.rangeOverweight,
    meaningKey: k.meaningOverweight,
  },
  {
    id: "obesity1",
    max: 35,
    labelKey: k.catObesity1,
    rangeKey: k.rangeObesity1,
    meaningKey: k.meaningObesity1,
  },
  {
    id: "obesity2",
    max: 40,
    labelKey: k.catObesity2,
    rangeKey: k.rangeObesity2,
    meaningKey: k.meaningObesity2,
  },
  {
    /** No upper bound; `Infinity` closes the walk in `bmiCategoryFor`. */
    id: "obesity3",
    max: Infinity,
    labelKey: k.catObesity3,
    rangeKey: k.rangeObesity3,
    meaningKey: k.meaningObesity3,
  },
] as const

export type BmiCategoryDef = (typeof CATEGORY_DEFS)[number]

export const CATEGORY_ORDER: readonly BmiCategoryDef[] = CATEGORY_DEFS

export const BMI_CATEGORIES: Record<BmiCategory, BmiCategoryDef> =
  Object.fromEntries(CATEGORY_DEFS.map((c) => [c.id, c])) as Record<
    BmiCategory,
    BmiCategoryDef
  >

/**
 * The BMI as it is shown to the reader: one decimal place.
 *
 * Callers must classify THIS value, not the raw one. Classifying the raw
 * value and displaying the rounded one lets the two disagree at a band edge:
 * a raw 24.96 displays as "25.0" but classifies as healthy, so the page would
 * print "25.0" next to "Healthy weight" and contradict its own reference
 * table. Rounding first means the label always describes the number on screen,
 * and it matches what a reader gets doing the arithmetic by hand from the
 * printed formula.
 */
export function roundBmi(bmi: number): number {
  return Math.round(bmi * 10) / 10
}

/**
 * Which category a BMI falls into. Boundaries are inclusive at the bottom of
 * each band: exactly 25.0 is overweight, exactly 30.0 is obesity class I.
 *
 * Pass a value from `roundBmi` — see the note there.
 */
export function bmiCategoryFor(bmi: number): BmiCategory {
  for (const category of CATEGORY_ORDER) {
    if (bmi < category.max) return category.id
  }
  return "obesity3"
}

/**
 * The exact weight span that puts someone of this height in the healthy BMI
 * range, in kilograms. Use `healthyWeightRangeForDisplay` to print it.
 */
export function healthyWeightRange(heightM: number): {
  lowKg: number
  highKg: number
} {
  const squared = heightM * heightM
  return {
    lowKg: HEALTHY_BMI_MIN * squared,
    highKg: HEALTHY_BMI_MAX * squared,
  }
}

/**
 * The healthy weight range rounded to whole units for display.
 *
 * Rounds INWARD on purpose: ceil the bottom, floor the top. Nearest-rounding
 * pushes the bounds outside the band it claims to describe — at 140 cm the
 * exact range is 36.3 to 48.8 kg, and `Math.round` prints "36 to 49 kg", two
 * numbers that both classify as something other than healthy weight. Every
 * weight in the printed range has to survive being fed back into the
 * calculator.
 */
export function healthyWeightRangeForDisplay(heightM: number): {
  low: number
  high: number
} {
  const { lowKg, highKg } = healthyWeightRange(heightM)
  return {
    low: Math.ceil(lowKg),
    high: Math.floor(highKg),
  }
}
